/**
 * "1x <what was booked>" for a locker booking summary.
 *
 * The compartment's name comes from the tenant's own group name, and the UI
 * used to append the word "Schließfach" / "Locker" to it unconditionally. That
 * reads correctly only when the tenant named the group after the SIZE alone
 * ("Größe M" → "1x Größe M Schließfach"). KGSt-Forum named its groups
 * "Schließfach Größe M", so the summary rendered
 *
 *     1x Schließfach Größe M Schließfach - 30Min.
 *
 * The word is therefore appended only when the name does not already carry it.
 * Dropping it outright was the alternative and was not taken: for a tenant whose
 * group really is called "Größe M", the bare "1x Größe M" says nothing about
 * what was booked.
 *
 * Matching is case- and whitespace-insensitive on the localised noun, which is
 * the same word the caller would have appended — so it cannot drift from it.
 *
 * An unknown `amount` drops the count rather than printing an invented "1": the
 * p2p ticket can be rendered before its reservation item carries one, and the
 * same rule already applies on the backend (`%ITEMS%` in
 * `orderService.sendOrderConfirmationByMail` prints the bare name when
 * `orderitem.amount` is null). Before this, the p2p ticket rendered a bare
 * "x Locker", because JSX prints `undefined` as nothing.
 */
export const bookedItemLabel = (
    amount: number | undefined,
    equipmentName: string | undefined,
    itemNoun: string
): string => {
    const count =
        typeof amount === "number" && amount > 0 ? `${amount}x ` : "";
    const name = (equipmentName ?? "").trim();
    if (!name) return `${count}${itemNoun}`;

    const noun = itemNoun.trim();
    const alreadyNamed =
        noun.length > 0 && name.toLowerCase().includes(noun.toLowerCase());

    return alreadyNamed ? `${count}${name}` : `${count}${name} ${noun}`;
};
